import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLPaper } from "OLPaper";
import { useTranslations } from "api/translations";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useParams } from "react-router-dom";
import { Nullable } from "types/global";
import { links } from "utilities/links";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    olPaper: {
      marginTop: theme.spacing(8),
    },
    table: {
      maxWidth: "100%",
      minWidth: 650,
      overflowWrap: "break-word",
      padding: theme.spacing(12),
      tableLayout: "fixed",
    },
    TextField: {
      width: "90%",
      "& input": {
        cursor: "pointer",
        fontSize: "0.875rem !important",
      },
    },
    tooltipTitle: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "24px",
      color: theme.gray800,
    },
  }),
);

export function EditTranslation() {
  const classes = useStyles();
  const { id: languageCode } = useParams<{ id: string }>();
  const {
    updateTranslation,
    useDefaultTranslations,
    useLanguageData,
    useTranslationsKeys,
  } = useTranslations();
  const { data: languageData } = useLanguageData(languageCode as string);
  const { data: defaultTranslations } = useDefaultTranslations();
  const { data: translationsKeys } = useTranslationsKeys();
  const { message } = useMessage();
  const [selectedKey, setSelectedKey] = useState<Nullable<string>>(null);
  const [currentValue, setCurrentValue] = useState<string>("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const createData = (
    name: string,
    selectedValue: string,
    defaultValue: string,
  ) => {
    return { name, selectedValue, defaultValue };
  };
  const rows =
    languageData && translationsKeys && defaultTranslations
      ? translationsKeys
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((singleKey: string) =>
            createData(
              singleKey,
              languageData.translations?.find(
                (translationKey: { key: string }) =>
                  translationKey.key === singleKey,
              )?.value,
              defaultTranslations?.[singleKey],
            ),
          )
      : [];

  return (
    <>
      <TopHeader
        title={
          languageData &&
          `${message({ id: "editTranslation" })} "${languageData?.name}"`
        }
        clearLink={links.settings}
      />
      <div>
        {rows.length ? (
          <OLPaper noPadding className={classes.olPaper}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell data-test-id="key-name">
                    {message({ id: "keyName" })}
                  </TableCell>
                  <TableCell data-test-id="key-name-value">{`${message({
                    id: "keyName",
                  })} (${languageCode}) ${message({
                    id: "value",
                  }).toLowerCase()}`}</TableCell>
                  <TableCell data-test-id="default-language-value">
                    {message({ id: "defaultLanguageValue" })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(
                  (
                    row: {
                      defaultValue: any;
                      name: string;
                      selectedValue: string;
                    },
                    index: number,
                  ) => (
                    <TableRow key={row.name}>
                      <TableCell
                        component="th"
                        scope="row"
                        data-test-id={`${row.name}-key-name-${index}-row`}
                      >
                        <Tooltip
                          text={
                            <Typography className={classes.tooltipTitle}>
                              {row.name}
                            </Typography>
                          }
                        >
                          <span>{row.name}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        onFocus={() => {
                          setSelectedKey(row.name);
                          setCurrentValue(row.selectedValue);
                        }}
                      >
                        <Tooltip
                          text={
                            row.selectedValue ? (
                              <Typography className={classes.tooltipTitle}>
                                {row.selectedValue}
                              </Typography>
                            ) : (
                              ""
                            )
                          }
                          placement="bottom-start"
                        >
                          <div>
                            <TextField
                              variant="standard"
                              className={classes.TextField}
                              defaultValue={row.selectedValue}
                              InputProps={{
                                disableUnderline: selectedKey !== row.name,
                              }}
                              inputProps={{
                                autoComplete: "off",
                                "data-test-id": `${row.name}-key-name-value-${index}-row`,
                              }}
                              onChange={(e: { target: { value: string } }) => {
                                setCurrentValue(e?.target?.value);
                              }}
                              onBlur={() => {
                                if (currentValue !== row.selectedValue) {
                                  updateTranslation(
                                    languageCode as string,
                                    row.name,
                                    currentValue,
                                  );
                                }
                                setSelectedKey(null);
                                setCurrentValue("");
                              }}
                            />
                          </div>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        data-test-id={`${row.name}-language-default-value-${index}-row`}
                      >
                        <Tooltip
                          text={
                            <Typography className={classes.tooltipTitle}>
                              {row.defaultValue}
                            </Typography>
                          }
                        >
                          <span>{row.defaultValue || "-"}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={translationsKeys.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </OLPaper>
        ) : (
          <Skeleton height={300} width={"100%"} />
        )}
      </div>
    </>
  );
}
